import baseUrlUtil from 'util/base-url-util';

import type * as React from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { createContext, useCallback, useEffect, useMemo, useState } from 'react';

import getCustomerCurrency from 'data/get-customer-currency';
import getCustomerInfo from 'data/get-customer-info';
import getFingerprint from 'data/get-fingerprint';
import isBlacklisted from 'data/is-blacklisted';
import { usePostHog } from 'posthog-js/react';

import type { Customer } from '../types';

interface ICustomerContext {
  blocked: boolean;
  fingerprint?: string;
  customer: Customer;
  id?: string;
  currency?: string | null;
  setCustomerId?: Dispatch<SetStateAction<string | undefined>>;
}

export const CustomerContext = createContext<ICustomerContext>({} as ICustomerContext);

const CustomerProvider: React.FC<React.ReactNode> = ({ children }) => {
  const posthog = usePostHog();

  const [fingerprint, setFingerprint] = useState<string | undefined>();
  const [blocked, setBlocked] = useState<boolean>(true);
  const [id, setId] = useState<string>();
  const [customer, setCustomer] = useState<Customer>({});
  const [currency, setCurrency] = useState<string | null>();

  useEffect(() => {
    getFingerprint().then(setFingerprint);
    getCustomerInfo().then((customerData) => {
      if (customerData) {
        setCustomer(customerData);
      }
    });
  }, []);

  useEffect(() => {
    const store = baseUrlUtil.getBaseUrl();
    if (fingerprint && posthog) {
      posthog?.identify(fingerprint || id, {
        fingerprint,
        id,
        store
      });
      posthog?.group('store', store);
    }
  }, [fingerprint, id]);

  useEffect(() => {
    const customerCurrency = getCustomerCurrency();
    setCurrency(customerCurrency || null);
  }, []);

  const isBlackListedCallBack: (ip: string, fingerprint: string) => Promise<boolean> = useCallback(
    async (ip: string, fingerprint: string): Promise<boolean> => await isBlacklisted({ fingerprint, ip }),
    [customer.ip, fingerprint]
  );

  useEffect(() => {
    if (customer && fingerprint && customer?.ip) {
      isBlackListedCallBack(customer.ip, fingerprint).then(setBlocked);
    }
  }, [customer, fingerprint, customer?.ip]);

  const initialValue = useMemo(
    () => ({ blocked, currency, customer, fingerprint, id, setCustomerId: setId }),
    [blocked, customer, id, fingerprint, currency]
  );

  return <CustomerContext.Provider value={initialValue}>{children}</CustomerContext.Provider>;
};
export default CustomerProvider;
